import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';
import PageHeader from './PageHeader';
import LabCard from './LabCard';
import AccentButton from './AccentButton';
import { cn } from '../../lib/utils';

const ACCENT_COLORS = [
  '#fedd58',
  '#8B5CF6',
  '#10B981',
  '#F59E0B',
  '#EC4899',
  '#3B82F6',
  '#EF4444',
  '#14B8A6',
  '#A855F7',
  '#F97316',
];

/**
 * Presentational resource list shell (header, tabs, search, grid).
 * No API calls — parent passes items/loading via hooks.
 */
export default function ResourceGridPage({
  title,
  subtitle,
  createLabel,
  createPath,
  onCreate,
  searchPlaceholder = 'Search by name',
  items = [],
  loading = false,
  getItemKey = item => item.id,
  getItemName = item => item.name || item.agent_name || item.title || '',
  getItemDescription = item => item.description || item.desc || 'No description.',
  getItemColor = (item, index) => item.icon_color || ACCENT_COLORS[index % ACCENT_COLORS.length],
  isOfficial = item => !!(item.is_official || item.is_public),
  getItemMenuItems,
  renderItem,
  mineContent,
  discoverContent,
  showDiscover = true,
  mineTabLabel,
  discoverTabLabel = 'Discover',
  emptyMessage,
  className,
  onItemClick,
}) {
  const [tab, setTab] = useState('mine');
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(item => {
      const name = getItemName(item)?.toLowerCase?.() || '';
      const desc = getItemDescription(item)?.toLowerCase?.() || '';
      return name.includes(q) || desc.includes(q);
    });
  }, [items, query, getItemName, getItemDescription]);

  const createButton =
    createLabel &&
    (createPath ? (
      <Link to={createPath}>
        <AccentButton>
          <Plus className="w-4 h-4" />
          {createLabel}
        </AccentButton>
      </Link>
    ) : (
      <AccentButton type="button" onClick={onCreate}>
        <Plus className="w-4 h-4" />
        {createLabel}
      </AccentButton>
    ));

  return (
    <div className={cn('p-8 lg:p-10', className)}>
      <PageHeader title={title} subtitle={subtitle} actions={createButton} />

      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        {showDiscover ? (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setTab('mine')}
              className={cn(
                'px-4 py-1.5 rounded-md text-sm transition-colors',
                tab === 'mine' ? 'bg-white/10 text-white' : 'text-muted-dim hover:text-white',
              )}>
              {mineTabLabel || `My ${title}`}
            </button>
            <button
              type="button"
              onClick={() => setTab('discover')}
              className={cn(
                'px-4 py-1.5 rounded-md text-sm transition-colors',
                tab === 'discover' ? 'bg-white/10 text-white' : 'text-muted-dim hover:text-white',
              )}>
              {discoverTabLabel}
            </button>
          </div>
        ) : (
          <div />
        )}

        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-faint" />
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full bg-[#1a1a1a] border border-shell-card-border rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder:text-muted-faint outline-none focus:border-primary/40"
          />
        </div>
      </div>

      {tab === 'discover' && showDiscover ? (
        typeof discoverContent === 'function' ? (
          discoverContent(query)
        ) : (
          discoverContent
        )
      ) : loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-40 rounded-lg bg-shell-card border border-shell-card-border animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-20 text-center text-muted-dim text-sm">
          {emptyMessage || `No ${title.toLowerCase()} found.`}
        </div>
      ) : typeof mineContent === 'function' ? (
        mineContent(filtered)
      ) : mineContent ? (
        mineContent
      ) : renderItem ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filtered.map((item, index) => (
            <React.Fragment key={getItemKey(item) ?? index}>{renderItem(item, index)}</React.Fragment>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filtered.map((item, index) => (
            <LabCard
              key={getItemKey(item) ?? index}
              as={onItemClick ? 'button' : 'div'}
              title={getItemName(item)}
              description={getItemDescription(item)}
              accentColor={getItemColor(item, index)}
              badge={isOfficial(item) ? 'official' : undefined}
              showMenu
              menuItems={getItemMenuItems ? getItemMenuItems(item) : undefined}
              onClick={onItemClick ? () => onItemClick(item) : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
}
